import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AdvancedMode } from "@/components/payroll/AdvancedMode";
import { Lightbulb, Scale, Sparkles, Settings2 } from "lucide-react";
import {
  getPayrollDataById,
  updateSocialChargesPercent,
} from "@/hooks/payrollDataService";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ViewMode = "start" | "advanced";

interface PayrollData {
  rooms: number;
  totals: {
    totalFTEs: string;
    annualCost: number;
    totalMonthly: number;
    socialCharges: number;
    monthlyPayroll: number;
  };
  savedAt: string;
  marketId: string;
  positions: PayrollPosition[];
  serviceStyle: string;
  socialChargesPercent: number;
}

interface PayrollPosition {
  included: boolean;
  positionId: string;
  departmentId: string;
  overrideFTEs: number | null;
  positionName: string;
  monthlySalary: number;
  calculatedFTEs: number;
  overrideSalary: number | null;
}

const DEPARTMENT_NAMES: Record<string, string> = {
  "508d5c3b-c493-43f9-a796-11558a7a1fc9": "Management",
  "8607eb3b-2103-4615-b96c-9eef9f282afa": "Admin & General",
  "0d04c044-87bd-4d24-9007-f0628ef6ee3a": "Front Office",
  "e156800b-55e8-4b3f-a5f9-64b8fe266512": "Housekeeping",
  "d93b774f-3906-47e9-ba2f-90675588f31c": "Food & Beverage",
  "559c7da5-01f0-4a2d-9764-ad7d6fb70220": "Spa & Wellness",
  "c5e5738e-0b31-4940-9b10-8ca654b9917f": "Maintenance",
};

const SERVICE_STYLE_OPTIONS = [
  {
    id: "efficient",
    label: "Efficient & Affordable",
    description: "Lean team, focused on essentials",
    icon: <Lightbulb className="h-6 w-6 text-amber-500" />,
  },
  {
    id: "balanced",
    label: "Comfortable & Balanced",
    description: "Standard hospitality staffing",
    icon: <Scale className="h-6 w-6 text-primary" />,
  },
  {
    id: "luxury",
    label: "Boutique Luxury",
    description: "Premium service levels",
    icon: <Sparkles className="h-6 w-6 text-amber-500" />,
  },
];

interface PayrollEntry {
  id: string;
  department: string;
  role: string;
  ftes: number;
  salary: number;
  socialCharges: number;
  totalCost: number;
}

interface SuggestedRole {
  id: string;
  department: string;
  role: string;
  ftes: number;
  salary: number;
  socialCharges: number;
  totalCost: number;
  reason: string;
}

interface PayrollProps {
  initialData?: any;
  dataId?: string;
  propertyId?: string;
  rooms?: number;
}

function extractPayrollData(rawData: any): PayrollData | null {
  if (!rawData) return null;
  if (rawData.data && typeof rawData.data === "object") {
    const inner = rawData.data;
    if (inner.rooms !== undefined && inner.positions)
      return inner as PayrollData;
  }
  if (rawData.rooms !== undefined && rawData.positions)
    return rawData as PayrollData;
  return null;
}

function transformPayrollData(data: PayrollData): {
  entries: PayrollEntry[];
  suggestedRoles: SuggestedRole[];
} {
  const entries: PayrollEntry[] = [];
  const suggestedRoles: SuggestedRole[] = [];

  if (!data.positions?.length) return { entries, suggestedRoles };

  data.positions.forEach((position) => {
    const departmentName = DEPARTMENT_NAMES[position.departmentId] || "Other";
    const socialChargesPercent = data.socialChargesPercent || 33.6;

    if (position.calculatedFTEs === 0) return;

    const totalCost =
      position.monthlySalary *
      position.calculatedFTEs *
      (1 + socialChargesPercent / 100);

    const entry: PayrollEntry = {
      id: position.positionId,
      department: departmentName,
      role: position.positionName,
      ftes: position.calculatedFTEs,
      salary: position.monthlySalary,
      socialCharges: socialChargesPercent,
      totalCost,
    };

    if (position.included) {
      entries.push(entry);
    } else {
      suggestedRoles.push({
        ...entry,
        reason: `Recommended for ${departmentName} department`,
      });
    }
  });

  return { entries, suggestedRoles };
}

function createEmptyPayrollData(rooms: number = 0): PayrollData {
  return {
    rooms,
    totals: {
      totalFTEs: "0",
      annualCost: 0,
      totalMonthly: 0,
      socialCharges: 0,
      monthlyPayroll: 0,
    },
    savedAt: new Date().toISOString(),
    marketId: "",
    positions: [],
    serviceStyle: "balanced",
    socialChargesPercent: 33.6,
  };
}

const Payroll = ({
  initialData,
  dataId,
  propertyId: propPropertyId,
  rooms = 0,
}: PayrollProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("start");
  const [payrollData, setPayrollData] = useState<PayrollData | null>(null);
  const [entries, setEntries] = useState<PayrollEntry[]>([]);
  const [suggestedRoles, setSuggestedRoles] = useState<SuggestedRole[]>([]);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resolvedPropertyId, setResolvedPropertyId] = useState<
    string | undefined
  >(propPropertyId);
  const [socialChargesPercent, setSocialChargesPercent] = useState(33.6);

  const isUpdatingFromEffect = useRef(false);
  const saveTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleEntriesChange = (newEntries: PayrollEntry[]) => {
    setEntries(newEntries);
  };

  const handleSocialChargesChange = (value: number) => {
    setSocialChargesPercent(value);

    const updatedEntries = entries.map((entry) => ({
      ...entry,
      socialCharges: value,
      totalCost: entry.salary * entry.ftes * (1 + value / 100),
    }));
    setEntries(updatedEntries);

    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      if (!resolvedPropertyId) return;
      try {
        await updateSocialChargesPercent(resolvedPropertyId, value);
      } catch (err) {
        console.error("Failed to save social charges:", err);
      }
    }, 800);
  };

  useEffect(() => {
    const fetchPayrollData = async () => {
      setIsLoading(true);
      try {
        let extractedData = extractPayrollData(initialData);

        if (!extractedData && dataId) {
          const apiData = await getPayrollDataById(dataId);
          if (apiData && (apiData as any).property_id) {
            setResolvedPropertyId((apiData as any).property_id);
          }
          extractedData = extractPayrollData(apiData);
        }

        if (extractedData) {
          setPayrollData(extractedData);
          setSocialChargesPercent(extractedData.socialChargesPercent || 33.6);
          const transformed = transformPayrollData(extractedData);
          setEntries(transformed.entries);
          setSuggestedRoles(transformed.suggestedRoles);
          if (transformed.entries.length > 0) setViewMode("advanced");
        } else {
          setPayrollData(createEmptyPayrollData(rooms));
        }
      } catch {
        setPayrollData(createEmptyPayrollData(rooms));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayrollData();
  }, [initialData, dataId, rooms]);

  useEffect(() => {
    if (!payrollData || isUpdatingFromEffect.current) return;

    const totalMonthlyCost = entries.reduce((sum, e) => sum + e.totalCost, 0);
    const totalAnnualCost = totalMonthlyCost * 12;
    const totalFTEs = entries.reduce((sum, e) => sum + e.ftes, 0);
    const monthlyPayroll = entries.reduce(
      (sum, e) => sum + e.salary * e.ftes,
      0,
    );
    const socialCharges = totalMonthlyCost - monthlyPayroll;

    const newTotals = {
      totalFTEs: totalFTEs.toFixed(1),
      annualCost: totalAnnualCost,
      totalMonthly: totalMonthlyCost,
      socialCharges,
      monthlyPayroll,
    };

    if (JSON.stringify(payrollData.totals) !== JSON.stringify(newTotals)) {
      isUpdatingFromEffect.current = true;
      setPayrollData((prev) => ({ ...prev!, totals: newTotals }));
      setTimeout(() => {
        isUpdatingFromEffect.current = false;
      }, 0);
    }
  }, [entries]);

  useEffect(() => {
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
    };
  }, []);

  const handleSelectStyle = (style: string) => {
    setSelectedStyle(style);
    setViewMode("advanced");
  };

  const handleAcceptSuggestion = (roleId: string) => {
    const suggestion = suggestedRoles.find((s) => s.id === roleId);
    if (!suggestion) return;
    setSuggestedRoles((prev) => prev.filter((s) => s.id !== roleId));
    handleEntriesChange([
      ...entries,
      {
        id: crypto.randomUUID(),
        department: suggestion.department,
        role: suggestion.role,
        ftes: suggestion.ftes,
        salary: suggestion.salary,
        socialCharges: socialChargesPercent,
        totalCost:
          suggestion.salary *
          suggestion.ftes *
          (1 + socialChargesPercent / 100),
      },
    ]);
  };

  const totalMonthlyCost = entries.reduce((sum, e) => sum + e.totalCost, 0);
  const totalAnnualCost = totalMonthlyCost * 12;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading payroll data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <p className="text-muted-foreground text-center">
            Configure your hotel staffing structure and cost projections
          </p>

          {payrollData && entries.length > 0 && (
            <div className="text-center text-sm text-muted-foreground mt-2">
              {payrollData.rooms} rooms • {entries.length} positions • €
              {totalMonthlyCost.toLocaleString()}/month
            </div>
          )}
        </div>

        {viewMode === "start" && entries.length === 0 && (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
                How would you describe your service level?
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {SERVICE_STYLE_OPTIONS.map((option) => (
                <Card
                  key={option.id}
                  className={`cursor-pointer transition-all hover:border-primary/50 hover:shadow-md ${
                    selectedStyle === option.id
                      ? "border-primary ring-2 ring-primary/20"
                      : ""
                  }`}
                  onClick={() => handleSelectStyle(option.id)}
                >
                  <CardContent className="pt-6 text-center">
                    <div className="flex justify-center mb-4">
                      {option.icon}
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {option.label}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {option.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {(viewMode === "advanced" || entries.length > 0) &&
          resolvedPropertyId && (
            <>
              <Card className="mb-6 p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Settings2 className="h-5 w-5" />
                    <Label htmlFor="social-charges" className="font-medium">
                      Social Charges Rate:
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="social-charges"
                      type="number"
                      value={socialChargesPercent}
                      onChange={(e) =>
                        handleSocialChargesChange(Number(e.target.value))
                      }
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-24 text-right"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-2">
                    Applies to all roles (social security, insurance, etc.)
                  </p>
                </div>
              </Card>

              <AdvancedMode
                propertyId={resolvedPropertyId}
                entries={entries}
                onEntriesChange={handleEntriesChange}
                totalMonthlyCost={totalMonthlyCost}
                totalAnnualCost={totalAnnualCost}
                suggestedRoles={suggestedRoles}
                onAcceptSuggestion={handleAcceptSuggestion}
                socialChargesPercent={socialChargesPercent}
                onSocialChargesChange={handleSocialChargesChange}
              />
            </>
          )}
      </div>
    </div>
  );
};

export default Payroll;
